import bb from 'billboard.js/dist/billboard.pkgd';
import {
  GetThreatsBreakdownResponse,
  IntValueWithEndpointType,
  Status,
  ThreatsBreakdown,
} from '@nirvana/shared/dtos/types/widgets/common';
import { TemplateState } from '../../types/common/types';
import { BackendResponseWithMeta, DASHBOARD_METADATA_KEY } from '../../types/widget/types';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import {
  CommonThreatsNavigationHelper,
  NavigationItem,
  ThreatsBreakdownNavigationHelper,
} from '@nirvana/shared/navigation';
import { BaseThreatWidgetView } from '../../widget/BaseThreatWidgetView';
import { ElementClass, elementClassListAdd, elementClassListRemove } from '../../utils/htmlElement.utils';

enum DataSets {
  Workstation,
  Servers,
  ContainerHosts,
  Containers,
}

export class ThreatsBreakdownView extends BaseThreatWidgetView {
  public shadow: any;
  protected translations: any;
  protected commonStrings: any;
  private $chart: any;
  private $style: any;
  private colors: {
    workstationNo: string;
    serversNo: string;
    containerHostsNo: string;
    containersNo: string;
    errorColor: string;
  };

  getDomElements() {
    super.getDomElements();
    this.dom.$widgetTitle = this.shadow.querySelector('.widget-title');
    this.dom.$widgetTitleString = this.shadow.querySelector('#threatsBreakdownTitle');
    this.dom.$threatsBreakdownList = this.shadow.querySelector('#threatsBreakdownList');
    this.dom.$chartContainer = this.shadow.querySelector('#threatsBreakdownChart');
    this.dom.$workstationName = this.shadow.querySelector(
      '[gz-threats-breakdown="workstationNo"] [gz-list-element="breakdownName"]'
    );
    this.dom.$serverName = this.shadow.querySelector(
      '[gz-threats-breakdown="serversNo"] [gz-list-element="breakdownName"]'
    );
    this.dom.$containerHostName = this.shadow.querySelector(
      '[gz-threats-breakdown="containerHostsNo"] [gz-list-element="breakdownName"]'
    );
    this.dom.$containerName = this.shadow.querySelector(
      '[gz-threats-breakdown="containersNo"] [gz-list-element="breakdownName"]'
    );
    this.dom.$style = getComputedStyle(document.body);
    this.getTranslations();
    this.setTranslatedStrings();
    this.setErrorTooltip();
    this.getChartColors();
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getThreatsBreakdownWidgetStrings();
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    const { title, workstationName, serverName, containerHostName, containerName } = this.translations;
    this.dom.$widgetTitleString.innerHTML = title;
    this.dom.$workstationName.innerHTML = workstationName;
    this.dom.$serverName.innerHTML = serverName;
    if (this.dom.$containerHostName) {
      this.dom.$containerHostName.innerHTML = containerHostName;
    }
    if (this.dom.$containerName) {
      this.dom.$containerName.innerHTML = containerName;
    }
  }

  buildErrorView() {
    this.resetTemplateState(this.$wrapper);
    this.setTemplateState(this.$wrapper, TemplateState.ERROR);
    this.buildEmptyTemplate();
  }

  private buildEmptyTemplate() {
    const listItems = this.shadow.querySelectorAll('.gz-data-item');
    listItems.forEach(item => {
      item.querySelector('[gz-list-element="breakdownValue"]').innerHTML = '0';
      item.querySelector('[gz-list-element="breakdownColor"]').style.backgroundColor = this.colors.errorColor;
    });
  }

  buildSuccessView(response: BackendResponseWithMeta<GetThreatsBreakdownResponse>) {
    this.resetTemplateState(this.$wrapper);
    let availableExtraFeatures: Array<string> = [];

    const result = response.widgetResult;

    if (result.availableExtraFeatures) {
      availableExtraFeatures = result.availableExtraFeatures;
    }

    if (result.status === Status.EMPTY_STATE) {
      this.buildEmptyTemplate();
      this.setTemplateState(this.$wrapper, TemplateState.EMPTY);
      this.showHideDomElementsDependingOnFeatures(availableExtraFeatures);
      return;
    }

    if (result.status === Status.ACTIVE && result.data) {
      this.latestData = result.data;
      this.buildList();
      this.showHideDomElementsDependingOnFeatures(availableExtraFeatures);
      this.buildChart();
      this.addDrillDownHandlers(
        response[DASHBOARD_METADATA_KEY].targets,
        response[DASHBOARD_METADATA_KEY].reportingPeriod.timeRange,
        response[DASHBOARD_METADATA_KEY].canDrillDown
      );
      return;
    }

    this.buildErrorView();
  }

  private getChartColors() {
    const wsColor = this.dom.$style.getPropertyValue('--red-chart-600').trimLeft();
    const srvColor = this.dom.$style.getPropertyValue('--purple-chart-050').trimLeft();
    const containerHostColor = this.dom.$style.getPropertyValue('--orange-alert-chart-100').trimLeft();
    const containerColor = this.dom.$style.getPropertyValue('--yellow-alert-050').trimLeft();
    const chartsErrorColor = this.dom.$style.getPropertyValue('--chart-101-pressed-dividers').trimLeft();

    this.colors = {
      workstationNo: wsColor,
      serversNo: srvColor,
      containerHostsNo: containerHostColor,
      containersNo: containerColor,
      errorColor: chartsErrorColor,
    };
  }

  private buildList() {
    for (const record in this.latestData) {
      if (this.latestData.hasOwnProperty(record)) {
        const $valueElement = this.shadow.querySelector(
          `[gz-threats-breakdown="${record}"] [gz-list-element="breakdownValue"]`
        );
        $valueElement.innerHTML = this.latestData[record].value;

        const $threatColor = this.shadow.querySelector(
          `[gz-threats-breakdown="${record}"] [gz-list-element="breakdownColor"]`
        );
        $threatColor.style.backgroundColor = this.colors[record];
      }
    }
  }

  private showHideDomElementsDependingOnFeatures(availableExtraFeatures: string[] = []) {
    let $element;
    $element = this.shadow.querySelector(`[gz-threats-breakdown="containerHostsNo"]`);
    elementClassListAdd($element, ElementClass.HIDDEN);
    $element = this.shadow.querySelector(`[gz-threats-breakdown="containersNo"]`);
    elementClassListAdd($element, ElementClass.HIDDEN);

    if (availableExtraFeatures.length && availableExtraFeatures.includes('ContainerProtection')) {
      $element = this.shadow.querySelector(`[gz-threats-breakdown="containerHostsNo"]`);
      elementClassListRemove($element, ElementClass.HIDDEN);
      $element = this.shadow.querySelector(`[gz-threats-breakdown="containersNo"]`);
      elementClassListRemove($element, ElementClass.HIDDEN);
    }
  }

  private renderBBChartTooltip([{ value, index }]): string {
    // tooltip used for the donut chart made with billboard.js library
    const { workstationName, serverName, containerHostName, containerName } = this.translations;
    const legend = [workstationName, serverName, containerHostName, containerName];

    return `
      <div class="chart-tooltip">
        <span class="name">${legend[index]}</span>
        <span class="value">${value}</span>
      </div>
    `;
  }

  private buildChart() {
    this.$chart = bb.generate({
      data: {
        columns: this.getBreakdownArray(this.latestData),
        type: 'donut',
        colors: {
          [DataSets.Workstation]: this.colors.workstationNo,
          [DataSets.Servers]: this.colors.serversNo,
          [DataSets.ContainerHosts]: this.colors.containerHostsNo,
          [DataSets.Containers]: this.colors.containersNo,
        },
      },
      donut: {
        title: this.getThreatsSum(this.latestData).toString(),
        label: {
          show: false,
        },
        width: 7,
        padAngle: 0.04,
      },
      expand: false,
      tooltip: {
        show: true,
        contents: this.renderBBChartTooltip.bind(this),
      },
      legend: {
        show: false,
      },
      bindto: this.dom.$chartContainer,
    });
  }

  private getBreakdownArray(data: ThreatsBreakdown): Array<any> {
    // create columns for chart
    const columns = [
      [DataSets.Workstation, data.workstationNo.value],
      [DataSets.Servers, data.serversNo.value],
    ];

    if (data.containerHostsNo) {
      columns.push([DataSets.ContainerHosts, data.containerHostsNo.value]);
    }

    if (data.containersNo) {
      columns.push([DataSets.Containers, data.containersNo.value]);
    }

    return columns.filter(column => column[1] > 0);
  }

  private getThreatsSum(data: ThreatsBreakdown): number {
    let sum = data.workstationNo.value + data.serversNo.value;

    if (data.containerHostsNo) {
      sum += data.containerHostsNo.value;
    }

    if (data.containersNo) {
      sum += data.containersNo.value;
    }
    return sum;
  }

  protected getNavigationItemForTitle(targets: Array<Target>, timeRange: number): NavigationItem {
    const endpointTypes = Object.values(this.latestData).map(
      (breakdown: IntValueWithEndpointType) => breakdown.endpointType
    );
    const isPartner = Application.Main.company.isPartner();
    const filters = ThreatsBreakdownNavigationHelper.getFiltersForNavigationItem({
      timeRange: timeRange,
      endpointTypes: endpointTypes,
      targets: targets,
      isPartner: isPartner,
    });
    const displayedColumns = ThreatsBreakdownNavigationHelper.getDisplayedColumns();

    return CommonThreatsNavigationHelper.buildNavigationItem([this.dom.$widgetTitle], filters, displayedColumns);
  }

  protected getNavigationItemsForWidgetBody(targets: Array<Target>, timeRange: number): Array<NavigationItem> {
    return Object.values(this.latestData).map((breakdown: IntValueWithEndpointType, listElementIndex: number) =>
      this.buildNavigationItemForListElement(breakdown, targets, timeRange, listElementIndex)
    );
  }

  private buildNavigationItemForListElement(
    breakdown: IntValueWithEndpointType,
    targets: Array<Target>,
    timeRange: number,
    elementIndex: number
  ) {
    const $listElem = this.dom.$threatsBreakdownList.children[elementIndex].querySelector('.item-details');
    const endpointTypes = [breakdown.endpointType];
    const isPartner = Application.Main.company.isPartner();
    const filters = ThreatsBreakdownNavigationHelper.getFiltersForNavigationItem({
      timeRange: timeRange,
      endpointTypes: endpointTypes,
      targets: targets,
      isPartner: isPartner,
    });
    const displayedColumns = ThreatsBreakdownNavigationHelper.getDisplayedColumns();

    return CommonThreatsNavigationHelper.buildNavigationItem([$listElem], filters, displayedColumns);
  }
}
